import { EquipmentModel } from './equipment.model';
import { OutOfCatalogStatusEnum, ReadAsEnum, TicketStatusEnum } from '../enums/ticketStatusEnum';
import { EstablishmentModel } from './establishment.model';
import { BuildingModel } from './building.model';
import { BuildingRoomModel } from './building-room.model';
import { BaseDataTableModel } from '../base/models/base-data-table.model';
import { UserProfileModel } from './user-profile.model';
import { RequirementModel } from './requirement.model';
import { CorrectiveInterventionModel } from './corrective-intervention.model';
import { ContractModel } from './contract.model';
import { EquipmentTypeModel } from './equipment-type.model';
import { TicketTypeEnum } from '../enums/ticket-type.enum';
import { RequestTypeEnum } from '../enums/request-type.enum';
import { CompetenceMotifEnum } from '../enums/competence-motif.enum';
import { WeekDayEnum } from '../enums/week-day.enum';
import { MetrikMotifRelationModel } from './metrik-motif.model';
import { ProductModel } from './product.model';
import { CustomerGroupComptableLine } from './customer-group.model';
import { PrestationModel } from './prestation.model';
import { CategoryModel } from './category.model';
import { OrderModel } from './order.model';
import { QuoteModel } from './quote-model';
import { BasketRowStatusEnum } from '../enums/basketRowStatusEnum';
import { UnavailabilityReasons } from '../interfaces/unavailability-reasons.interface';
import { TicketBasketSummary } from '../interfaces/ticket-basket-summary.interface';
import { TicketSubStatusEnum } from '../enums/ticketSubStatusEnum';
import { AddressModel } from './address.model';
import { GenericProductModel } from './generic-product.model';
import { GenericEquipmentModel } from './generic-equipment.model';
import { TicketIncidentSubStatusEnum } from '../enums/ticketIncidentSubStatus';
import { InterventionStatusEnum } from '../enums/intervention-status.enum';
import { GenericPrestationModel } from './generic-prestation.model';
import { ActivityDistributionEntry } from '../interfaces/activity-distribution.interface';

export interface TicketLinePermission {
  _id: string;
  isEditable: boolean;
  isDeletable: boolean;
}

/**
 * Famille d'achat majoritaire d'un panier : la famille de niveau 1 dont le cumul TTC est le plus
 * élevé. C'est elle qui pilote la résolution des acteurs (validateurs, superviseur). Diagnostic
 * affiché aux seuls comptes Predik.
 */
export interface TicketMainPurchaseFamily {
  _id: string;
  label: string;
  totalTTC: number;
}

export interface TicketPermissions {
  role: ReadAsEnum;
  canEdit: boolean;
  canClose: boolean;
  canDelete: boolean;
  canRestore: boolean;
  canSendToAuthorization: boolean;
  canAuthorizeTicket: boolean;
  canAuthorizeBasket: boolean;
  canConfirmBasket: boolean;
  canDelegateSupervision: boolean;
  canCancelBasket: boolean;
  canResendBasketToOrderManagement: boolean;
  canProposeBasket: boolean;
  canRefuseBasket: boolean;
  canGiveUpTicket: boolean;
  canGenerateQuoteDemand: boolean;
  canQualifyAnomaly: boolean;
  canReadInternalComment: boolean;
  canUpdateGeneric: boolean;
  canCreateOutOfCatalog: boolean;
  basketLinePermissions: TicketLinePermission[];
}

export class TicketModel extends BaseDataTableModel {
  _id?: string;
  location?: EquipmentLocationModel;
  equipment?: EquipmentModel;
  orders?: OrderModel[];
  quotes?: QuoteModel[];
  type?: TicketTypeEnum;
  description?: string;
  locationDescription?: string;
  outOfCatalogDescription?: string;
  status?: TicketStatusEnum;
  subStatus?: TicketSubStatusEnum;
  incidentSubStatus?: TicketIncidentSubStatusEnum;
  displayStatus?: string;
  requestTypeDisplay?: string;
  technicalIdentifier?: string;
  dateSend?: Date;
  createdBy?: UserProfileModel;
  requirements?: RequirementModel[];
  requirement?: RequirementModel;
  correctiveInterventions?: CorrectiveInterventionModel[];
  preventiveInterventions?: CorrectiveInterventionModel[];
  dateSendToSupplier?: Date;
  report?: string;
  statusHistory?: StatusHistory[];
  haveContract?: boolean;
  contract?: ContractModel[];
  reason?: string;
  technicalQualification?: CorrectiveInterventionModel;
  equipmentType?: EquipmentTypeModel;
  category?: CategoryModel;
  permissions?: TicketPermissions;
  isOrderable?: boolean;
  comments?: CommentModel[];
  delegatedValidator?: UserProfileModel;
  supervision?: UserProfileModel;
  // descriptionClient
  customersComments?: CustomerCommentModel[];

  lastStatusUpdate?: Date;
  establishment?: EstablishmentModel;
  categories?: string[];
  deliveryAddress?: AddressModel;
  deliveryEstablishment?: { _id: string; label: string };
  destination?: string;
  isContract?: boolean;
  genericProduct?: GenericProductModel;
  genericEquipment?: GenericEquipmentModel;
  anomalyType?: any;
  selectedRecurringBasket?: any;
  isArchive?: boolean;
  isDelete?: boolean;

  outOfCatalogTreatment?: OutOfCatalogTreatmentModel;
  isFinalized?: boolean;
  isUpdatingLines?: boolean;
  orIsCreatedByCurrentUser?: boolean;
  createdAt?: Date;
  fullName?: string;
  hasOutOfCatalogTreatment?: boolean;
  equipmentBuildingReference?: string;

  urgentComment?: string;

  basket?: TicketBasketModel;
  activityDistribution?: ActivityDistributionEntry[];
  // Calculé côté back (generateFindOneByIdAggregate) : true si au moins une
  // commande est liée au ticket → la répartition par activité devient figée.
  hasOrder?: boolean;

  // PRED-3065 — "Compétence" request type
  requestType?: RequestTypeEnum;
  /** Legacy PRED-3065 : motif en dur des tickets antérieurs à PRED-3288 (lecture seule). */
  competenceMotif?: CompetenceMotifEnum;
  /** PRED-3288 : motif Metrik typé Compétence (référentiel Core). */
  metrikMotif?: MetrikMotifRelationModel;
  justification?: string;
  periodStart?: Date;
  periodEnd?: Date;

  // PRED-3286 — jours travaillés + heures quotidiennes ; total calculé par le back (absent sur les tickets antérieurs)
  workedDays?: WeekDayEnum[];
  dailyHours?: number;
  competenceHours?: number;
}

/** Champs du calcul d'heures d'un ticket COMPETENCE (prévisualisation et édition Motif / Usage). */
export interface CompetenceSchedulePayload {
  periodStart: Date;
  periodEnd: Date;
  workedDays: WeekDayEnum[];
  dailyHours: number;
}

export interface CompetenceHoursPreview {
  workedDaysCount: number;
  hours: number;
}

export class StatusHistory {
  status: TicketStatusEnum;
  title: string;
  subStatus?: TicketSubStatusEnum;
  text: string;
  createdAt: Date;
  createdBy: UserProfileModel;
}

export class OutOfCatalogTreatmentModel {
  description: string;
  status: OutOfCatalogStatusEnum;
  genericObject?: GenericProductModel | GenericPrestationModel;
  deliveryAddress?: AddressModel;
  destination?: string;
  reference?: ProductModel | PrestationModel;
  isCreatorTreatment?: boolean;
}

export class EquipmentLocationModel {
  establishment: EstablishmentModel;
  building: BuildingModel;
  room: BuildingRoomModel;
}

export class TicketCommentModel {
  _id?: string;
  comment: string;
  actions?: CommentActionModel[];
  createdBy?: UserProfileModel;
  createdAt?: Date;
  updatedAt?: Date;

  isUpdateFormOpen?: boolean;
  isUrgent?: boolean;

  isDelete?: boolean;
  isReplace?: boolean;

  isWaiting?: boolean;
  reference?: string;
}

export class TicketBasketModel {
  _id?: string;
  products: BasketProduct[];
  prestations: BasketPrestation[];
  groupedBySupplier?: any; // Structure groupée par fournisseur depuis le backend
  summary?: TicketBasketSummary; // Récapitulatif pré-calculé par le backend
  updatedAt?: Date;
  createdAt?: Date;
}

export class BasketProduct extends ProductModel {
  item?: ProductModel | string;
  referenceId?: string;
  accountingAccount: CustomerGroupComptableLine;
  _idLine: string;
  dataTableSelectDisabled: boolean;
  stock?: number;
  quoteId?: string;
  status?: BasketRowStatusEnum;
  unavailabilityReasons?: UnavailabilityReasons;
  isUrgent?: boolean;
  isAllowed: boolean;
  isOutOfCatalog?: boolean;
  priceHistory: any;
  isEditable?: boolean;
  isDeletable?: boolean;
  orderId?: any;
}

export class BasketPrestation extends PrestationModel {
  item?: string | PrestationModel;
  referenceId?: string;
  selectedContract: ContractModel;
  accountingAccount: CustomerGroupComptableLine;
  _idLine: string;
  dataTableSelectDisabled: boolean;
  quoteId?: string;
  status?: BasketRowStatusEnum;
  unavailabilityReasons?: UnavailabilityReasons;
  isUrgent?: boolean;
  isAllowed: boolean;
  isOutOfCatalog?: boolean;
  priceHistory: any;
  interventionId?: string;
  isEditable?: boolean;
  isDeletable?: boolean;
  orderId?: any;

  interventionStatus?: InterventionStatusEnum;
}

export enum CommentTicketEnum {
  TICKET_CREATOR = 'TICKET_CREATOR',
  SUPERVISION = 'SUPERVISION',
  REFERENT_QUALITY = 'REFERENT_QUALITY',
  TICKET_EMERGENCY = 'TICKET_EMERGENCY',
  //  REFERENT_METIER = 'REFERENT_METIER',
  PURCHASER = 'PURCHASER',
  REFERENT_ENGAGEMENT = 'REFERENT_ENGAGEMENT',
}

export interface CommentDataModel {
  name: string;
  type: CommentTicketEnum;
}

export interface CommentModel {
  _id: string;
  comment: string;
  actions: CommentActionModel[];
  createdBy: UserProfileModel;
  createdAt: Date;
  updatedAt: Date;
  isUpdateFormOpen: boolean;
  isUrgent: boolean;
}

export interface CustomerCommentModel {
  _id: string;
  comment: string;
  actions: CommentActionModel[];
  createdBy: UserProfileModel;
  createdAt: Date;
  updatedAt: Date;
  isUpdateFormOpen: boolean;
  isUrgent: boolean;
}

export interface CommentActionModel {
  type: CommentTicketEnum;
  visible: boolean;
  notification: boolean;
}
